import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { apiRequest, TOKEN_KEY } from "../services/api";
import type { AdminIdentity, AdminRole } from "../types/api";

const IDENTITY_KEY = "yma-admin-identity";

export const useAuthStore = defineStore("auth", () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || "");
  const stored = localStorage.getItem(IDENTITY_KEY);
  const identity = ref<AdminIdentity | null>(stored ? JSON.parse(stored) as AdminIdentity : null);
  const isAuthenticated = computed(() => Boolean(token.value));
  const canOperate = computed(() => ["SUPER_ADMIN", "ADMIN", "OPERATOR"].includes(identity.value?.role || ""));
  const canManageContent = computed(() => ["SUPER_ADMIN", "ADMIN"].includes(identity.value?.role || ""));
  const isSuperAdmin = computed(() => identity.value?.role === "SUPER_ADMIN");

  async function login(username: string, password: string) {
    const result = await apiRequest<{ accessToken: string; admin: AdminIdentity }>("/auth/admin/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ username, password })
    });
    token.value = result.accessToken;
    identity.value = result.admin;
    localStorage.setItem(TOKEN_KEY, result.accessToken);
    localStorage.setItem(IDENTITY_KEY, JSON.stringify(result.admin));
  }

  function logout() {
    token.value = "";
    identity.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(IDENTITY_KEY);
  }

  function roleLabel(role?: AdminRole) {
    return ({ SUPER_ADMIN: "超级管理员", ADMIN: "管理员", OPERATOR: "服务专员", VIEWER: "只读成员" } as const)[role || "VIEWER"];
  }

  return { token, identity, isAuthenticated, canOperate, canManageContent, isSuperAdmin, login, logout, roleLabel };
});
